import { Model, ModelAttributes, model, modelConfig, reference } from "@mixitone/oom";
import { setAccountId } from "./concerns/SetAccountId";
import Club from "./Club";
import ClubPlayer from "./ClubPlayer";
import RecurringSession from "./RecurringSession";

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    attending: {
      type: "boolean",
    },
    club_id: {
      type: "string",
    },
    club_player_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    is_deleted: {
      type: "boolean",
    },
    night_id: {
      type: "string",
    },
    recurring_session_id: {
      type: "string",
    },
    session_date: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
  },
  {},
  {
    club: reference<Club>(() => Club, "club_id"),
    clubPlayer: reference<ClubPlayer>(() => ClubPlayer, "club_player_id"),
    recurringSession: reference<RecurringSession>(() => RecurringSession, "recurring_session_id"),
  },
);

interface SessionIntent extends ModelAttributes<typeof config> {}

@setAccountId
@model("session_intents", config)
class SessionIntent extends Model<typeof config> {
  static override tableName: "session_intents" = "session_intents";
  static override config = config;

  /** UTC fallback helper; timezone-aware variants should be preferred for v1 behavior. */
  static formatUtcDateString(utcMillis: number): string {
    return new Date(utcMillis).toISOString().slice(0, 10);
  }

  /** MixItOne recurring_sessions.weekday: Monday = 1 … Sunday = 7 */
  static utcCalendarWeekdayMixitone(utcMillis: number): number {
    const dow = new Date(utcMillis).getUTCDay();
    return dow === 0 ? 7 : dow;
  }

  static parseSessionStartUtcMillis(sessionDateYmd: string, startTime: string | null | undefined): number {
    const parts = (startTime ?? "00:00:00").split(":");
    const hh = Number.parseInt(parts[0] ?? "0", 10);
    const mm = Number.parseInt(parts[1] ?? "0", 10);
    const ss = Number.parseInt(parts[2] ?? "0", 10);
    const [y, mo, d] = sessionDateYmd.split("-").map((x) => Number.parseInt(x, 10));
    return Date.UTC(y, mo - 1, d, hh, mm, ss);
  }

  /** Normalize portal/sync session_date values (YYYY-MM-DD or ISO timestamp) to YMD. */
  static sessionDateYmd(sessionDate: string | null | undefined): string | null {
    if (!sessionDate) return null;
    return sessionDate.slice(0, 10);
  }

  /** Attending RSVPs for one recurring session occurrence (club-local calendar day). */
  static async countAttendingForOccurrence(
    clubId: string,
    recurringSessionId: string,
    sessionLocalDate: string,
  ): Promise<number> {
    const rows = await SessionIntent.query()
      .eq("club_id", clubId)
      .eq("recurring_session_id", recurringSessionId)
      .gte("session_date", sessionLocalDate)
      .eq("attending", true)
      .eq("is_deleted", false)
      .all();

    return rows.filter((row) => SessionIntent.sessionDateYmd(row.session_date) === sessionLocalDate).length;
  }

  /** Next upcoming instance for a weekly recurring session; instance start must be strictly after `now`. */
  static nextSessionIntentTargetDateUtc(
    weekday: number,
    startTime: string | null | undefined,
    now: Date = new Date(),
  ): string | null {
    const base = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    for (let d = 0; d < 14; d++) {
      const dayMs = base + d * 86400000;
      if (SessionIntent.utcCalendarWeekdayMixitone(dayMs) !== weekday) continue;
      const ymd = SessionIntent.formatUtcDateString(dayMs);
      const startMs = SessionIntent.parseSessionStartUtcMillis(ymd, startTime);
      if (startMs > now.getTime()) return ymd;
    }
    return null;
  }
}

export default SessionIntent;
